import { UserType } from '@/core/domain/yCarsAuth/User/User.types';
import { Alert, Button, Modal, Tooltip, Typography } from 'antd';
import { UserYCars, YCarsStatus } from '@/types/YCars';
import SentEmailValidateCodeYcarsModal from '../SentEmailValidateCodeYcarsModal';
import { useDisclosure, useWindowSize } from '@/hooks';
import {
  useLinkUserMatrix,
  useUnlinkUserMatrix,
} from '@/core/domain/yCarsAuth/User/User.hooks';
import { getUserFromLocalStorage } from '@/utils/user';
import Group from '../Group';
import { Info } from 'react-feather';

interface Props {
  ycarsStatus?: YCarsStatus;
  user: UserYCars;
}

export default function SentEmailValidateCodeYcarsAlert({
  ycarsStatus,
  user,
}: Props) {
  const { Text, Title } = Typography;
  const { matrixId } = getUserFromLocalStorage();
  const { isMobile } = useWindowSize();

  const [sentEmailOpened, sentEmailActions] = useDisclosure();

  const linkUserMatrix = useLinkUserMatrix();
  const unlinkUserMatrix = useUnlinkUserMatrix();

  const header = (
    <Group position="between" style={{ marginBottom: 12 }}>
      <Title level={5} style={{ lineHeight: 1 }}>
        Área do cliente
      </Title>

      <Tooltip
        title="Área do cliente desenvolvida para o cliente acompanhar as 
            movimentações dos seus veículos, contas a pagar e realizar pagamentos de boletos."
        placement="left"
        trigger={isMobile ? 'click' : 'hover'}
      >
        <Info size={20} cursor="pointer" />
      </Tooltip>
    </Group>
  );

  const handleLinkUserMatrix = async () => {
    Modal.confirm({
      title: 'Vínculo',
      content: `Deseja realmente vincular a conta do cliente a sua matrix?`,
      onOk: async () => {
        await linkUserMatrix.mutateAsync({
          matrixId: Number(matrixId),
          userId: user.id,
          userType: UserType.customer,
          cpfCnpj: user.cpfCnpj,
        });
      },
    });
  };

  const handleUnlinkUserMatrix = async () => {
    Modal.confirm({
      title: 'Remover vínculo',
      content:
        'Se você remover o acesso do cliente, ele será desvinculado da matriz no ' +
        'Área do Cliente. Deseja continuar? Este processo é irreversível.',
      onOk: async () => {
        await unlinkUserMatrix.mutateAsync({
          userId: user.id,
          userType: UserType.customer,
          cpfCnpj: user.cpfCnpj,
        });
      },
    });
  };

  const InactiveComponent: React.FC = () => (
    <Alert
      type="warning"
      message={
        <>
          {header}

          <div style={{ marginBottom: 12 }}>
            Ao clicar no botão abaixo irá abrir uma janela onde você poderá
            digitar o e-mail do cliente ou selecionar um já existente em
            contatos para enviar o código de verificação necessário para
            <strong> ativar a conta na Área do Cliente.</strong>
          </div>

          <Button type="primary" onClick={sentEmailActions.open}>
            Enviar e-mail
          </Button>
        </>
      }
      style={{ marginBottom: 24, borderRadius: 8, padding: 14 }}
    />
  );

  const PendingComponent: React.FC = () => (
    <Alert
      type="success"
      message={
        <>
          {header}

          <div style={{ marginBottom: 12 }}>
            <Text>
              Não recebeu o e-mail? Clique no botão abaixo para abrir uma janela
              onde você pode digitar o e-mail do cliente ou selecionar um dos
              contatos existentes para reenviar o código de verificação e
              <strong> ativar a conta na Área do Cliente.</strong>
            </Text>
          </div>

          <Button type="primary" onClick={sentEmailActions.open}>
            Reenviar e-mail
          </Button>
        </>
      }
      style={{ marginBottom: 24, borderRadius: 8, padding: 14 }}
    />
  );

  const ActiveComponent: React.FC = () => (
    <Alert
      type="success"
      message={
        <>
          {header}

          <div style={{ marginBottom: 12 }}>
            <Text style={{ marginBottom: 12, fontWeight: 500 }}>
              Cliente vinculado a plataforma!
            </Text>
          </div>

          <Button.Group>
            <Button type="primary" onClick={handleUnlinkUserMatrix}>
              Remover Vínculo
            </Button>

            <Tooltip
              trigger={isMobile ? 'click' : 'hover'}
              title="Remover o vínculo fará com que o cliente não veja mais nenhuma informação relacionada às suas empresas"
              placement="right"
            >
              <Button type="primary">
                <Info size={20} type="primary" />
              </Button>
            </Tooltip>
          </Button.Group>
        </>
      }
      style={{ marginBottom: 24, borderRadius: 8, padding: 14 }}
    />
  );

  const AlreadyRegistredComponent: React.FC = () => (
    <Alert
      type="warning"
      message={
        <>
          {header}

          <div style={{ marginBottom: 12 }}>
            <Text style={{ marginBottom: 12, fontWeight: 500 }}>
              O cliente já está cadastrado na plataforma. Ao clicar no botão
              abaixo, ele será vinculado às suas empresas e poderá acompanhar as
              movimentações dos veículos, as contas a pagar e realizar pagamento
              de boletos.
            </Text>
          </div>

          <Button type="primary" onClick={handleLinkUserMatrix}>
            Vincular cliente
          </Button>
        </>
      }
      style={{ marginBottom: 24 }}
    />
  );

  const getStatusComponent = (status?: YCarsStatus) => {
    switch (status) {
      case YCarsStatus.pending:
        return <PendingComponent />;
      case YCarsStatus.active:
        return <ActiveComponent />;
      case YCarsStatus.alreadyRegistred:
        return <AlreadyRegistredComponent />;
      default:
        return <InactiveComponent />;
    }
  };

  return (
    <>
      {getStatusComponent(ycarsStatus)}

      <SentEmailValidateCodeYcarsModal
        open={sentEmailOpened}
        onCancel={sentEmailActions.close}
        user={user}
      />
    </>
  );
}
